import type { AuthClientConfig, RefreshOutcome } from "./types";
import type { CrossTab } from "./crosstab";

/**
 * Rafraîchissement silencieux, dédupliqué à deux niveaux.
 *
 * Le refresh token est à USAGE UNIQUE et rotatif, avec détection de réutilisation
 * par famille côté serveur : rejouer le même jeton en parallèle forke la famille
 * et, au-delà de la fenêtre de grâce, la fait révoquer — ce qui déconnecte TOUS
 * les onglets d'un coup. D'où :
 *   1. Single-flight INTRA-onglet (`inflight`) : une rafale de 401 ne déclenche
 *      qu'un seul refresh dans cet onglet.
 *   2. Verrou INTER-onglets (`navigator.locks`, portée origine) + horodatage
 *      partagé (localStorage) : un seul onglet appelle réellement le serveur ;
 *      les suiveurs constatent le refresh récent et réutilisent le cookie
 *      d'accès déjà renouvelé — partagé entre onglets — sans rejouer l'appel.
 *
 * Limite assumée : verrou, localStorage et canal sont à portée d'ORIGINE. Deux
 * apps sur deux sous-domaines (SaaS + console staff) ne se coordonnent pas entre
 * elles ; c'est la fenêtre de grâce de rotation côté serveur qui absorbe ces
 * courses-là.
 */

/**
 * Un refresh plus récent que ça ⇒ le cookie d'accès (1 h) est encore tout frais,
 * un suiveur peut le réutiliser tel quel.
 *
 * 10 s au départ, porté à 60 s : c'était trop court face à la latence réelle de
 * la file d'attente de `navigator.locks` quand l'onglet suiveur est en
 * arrière-plan (timers throttlés par le navigateur). Le suiveur reprenait alors
 * la main après la fenêtre, rappelait le serveur et re-forkait la famille —
 * précisément ce que le verrou devait empêcher. Réutiliser un cookie rafraîchi
 * il y a 60 s ne coûte rien : il lui reste 59 minutes de validité.
 */
const RECENT_REFRESH_MS = 60 * 1000;

/**
 * Sans ça, un serveur qui ne répond jamais laisse la promesse `inflight`
 * suspendue indéfiniment : toutes les requêtes suivantes s'y accrochent et
 * l'app se fige au lieu de dégrader proprement.
 */
const REFRESH_TIMEOUT_MS = 15 * 1000;

export interface Refresher {
  tryRefreshSession(): Promise<RefreshOutcome>;
  markLoggedOut(): void;
  lastRefreshAt(): number | null;
}

export function createRefresher(config: AuthClientConfig, crossTab: CrossTab): Refresher {
  const namespace = config.namespace ?? "constrigo";
  const lastRefreshKey = `${namespace}:auth:lastRefreshAt`;
  const lockName = `${namespace}:auth-refresh`;

  let inflight: Promise<RefreshOutcome> | null = null;

  function readLastRefresh(): number | null {
    try {
      const raw = Number(localStorage.getItem(lastRefreshKey) || 0);
      return raw > 0 ? raw : null;
    } catch {
      return null;
    }
  }

  function markRefreshed(): void {
    try {
      localStorage.setItem(lastRefreshKey, String(Date.now()));
    } catch {
      // localStorage indisponible (mode privé, quota) — best-effort.
    }
  }

  function clearRefreshMark(): void {
    try {
      localStorage.removeItem(lastRefreshKey);
    } catch {
      // Idem.
    }
  }

  function refreshedRecently(): boolean {
    const last = readLastRefresh();
    return last !== null && Date.now() - last < RECENT_REFRESH_MS;
  }

  async function doRefresh(): Promise<RefreshOutcome> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
    try {
      // fetch direct, hors client tRPC batché : le cookie de refresh est
      // restreint au path /trpc/auth.refresh, un appel batché ne l'enverrait pas.
      const res = await fetch(`${config.trpcUrl}/auth.refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-constrigo-app": config.appHeader },
        credentials: "include",
        body: JSON.stringify({}),
        signal: controller.signal,
      });

      // Verdict explicite du serveur : le refresh token est mort (révoqué,
      // expiré, non tracké) ou l'accès à l'app a été retiré.
      if (res.status === 401 || res.status === 403) {
        clearRefreshMark();
        return "unauthenticated";
      }
      // 429, 5xx, 502 HTML pendant un redéploiement… : statut INCONNU.
      if (!res.ok) return "unavailable";

      const data = await res.json().catch(() => null);
      if (data?.result?.data?.token) {
        markRefreshed();
        crossTab.post({ type: "refreshed", at: Date.now() });
        return "refreshed";
      }
      // Corps d'erreur renvoyé avec un statut 2xx (rare, mais l'enveloppe tRPC
      // le permet) : on ne conclut à la déconnexion que si le code le dit.
      const code = data?.error?.data?.code;
      if (code === "UNAUTHORIZED" || code === "FORBIDDEN") {
        clearRefreshMark();
        return "unauthenticated";
      }
      return "unavailable";
    } catch {
      // Coupure réseau, DNS, CORS, timeout (abort) — surtout PAS une déconnexion.
      return "unavailable";
    } finally {
      clearTimeout(timer);
    }
  }

  async function coordinatedRefresh(): Promise<RefreshOutcome> {
    const locks =
      typeof navigator !== "undefined"
        ? (navigator as Navigator & { locks?: LockManager }).locks
        : undefined;

    if (locks?.request) {
      try {
        return await locks.request(lockName, async () => {
          // Un autre onglet a pu rafraîchir pendant qu'on attendait le verrou.
          if (refreshedRecently()) return "refreshed";
          return doRefresh();
        });
      } catch {
        // Web Locks indisponible / verrou annulé — repli best-effort horodaté.
        if (refreshedRecently()) return "refreshed";
        return doRefresh();
      }
    }

    // Navigateur sans Web Locks : au moins le garde-fou horodaté partagé.
    if (refreshedRecently()) return "refreshed";
    return doRefresh();
  }

  return {
    tryRefreshSession(): Promise<RefreshOutcome> {
      if (!inflight) {
        inflight = coordinatedRefresh().finally(() => {
          inflight = null;
        });
      }
      return inflight;
    },

    /**
     * À appeler sur toute déconnexion (volontaire ou subie). Purger
     * l'horodatage est indispensable : sinon un autre onglet le voit « récent »,
     * croit qu'un refresh vient d'aboutir, rejoue sa requête, reprend un 401 et
     * reste bloqué — ni connecté, ni redirigé.
     */
    markLoggedOut(): void {
      clearRefreshMark();
      crossTab.post({ type: "logout" });
    },

    lastRefreshAt: readLastRefresh,
  };
}
